import { useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'

export type SprintReportPageState = {
  aggregateField: AggregateField
  // 集計対象: タスク / 成果物 (PBL)。デフォルト TASK で既存挙動を維持する。
  aggregateTarget: AggregateTarget
  workloadUnit: WorkloadUnit
  rowHeight: number
}

export type SprintReportPageStateApi = SprintReportPageState & {
  initialized: boolean
  setAggregateField: (value: AggregateField) => void
  setAggregateTarget: (value: AggregateTarget) => void
  setWorkloadUnit: (value: WorkloadUnit) => void
  setRowHeight: (value: number) => void
}

const DEFAULT_STATE: SprintReportPageState = {
  aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
  // AggregateTarget は WbsItemType.TASK | WbsItemType.DELIVERABLE の部分集合型なので
  // WbsItemType.TASK で代入する。デフォルトを TASK にすることで旧 SprintReport の挙動を維持する。
  aggregateTarget: WbsItemType.TASK,
  workloadUnit: WorkloadUnit.HOUR,
  rowHeight: ROW_HEIGHT.SMALL,
}

export const usePageState = (
  functionUuid: string
): SprintReportPageStateApi => {
  const [aggregateField, setAggregateField] = useState<AggregateField>(
    DEFAULT_STATE.aggregateField
  )
  const [aggregateTarget, setAggregateTarget] = useState<AggregateTarget>(
    DEFAULT_STATE.aggregateTarget
  )
  const [workloadUnit, setWorkloadUnit] = useState<WorkloadUnit>(
    DEFAULT_STATE.workloadUnit
  )
  const [rowHeight, setRowHeight] = useState<number>(DEFAULT_STATE.rowHeight)
  const [initialized, setInitialized] = useState(false)

  // 初回マウント時に uistate から復元
  useEffect(() => {
    const restore = async () => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
        })
        const stored = response.json
        if (stored?.value) {
          const payload: Partial<SprintReportPageState> = JSON.parse(
            stored.value
          )
          if (payload.aggregateField !== undefined) {
            setAggregateField(payload.aggregateField)
          }
          if (payload.aggregateTarget !== undefined) {
            setAggregateTarget(payload.aggregateTarget)
          }
          if (payload.workloadUnit !== undefined) {
            setWorkloadUnit(payload.workloadUnit)
          }
          if (payload.rowHeight !== undefined) {
            setRowHeight(payload.rowHeight)
          }
        }
      } finally {
        setInitialized(true)
      }
    }
    restore()
  }, [functionUuid])

  // 変更時に uistate に保存
  useEffect(() => {
    if (!initialized) return
    uiStates.update(
      {
        key: UiStateKey.PageState,
        scope: UiStateScope.User,
        value: JSON.stringify({
          aggregateField,
          aggregateTarget,
          workloadUnit,
          rowHeight,
        }),
      },
      functionUuid
    )
  }, [
    initialized,
    aggregateField,
    aggregateTarget,
    workloadUnit,
    rowHeight,
    functionUuid,
  ])

  // useState の setter は React により安定参照が保証されているため、そのまま公開する。
  return useMemo(
    () => ({
      initialized,
      aggregateField,
      aggregateTarget,
      workloadUnit,
      rowHeight,
      setAggregateField,
      setAggregateTarget,
      setWorkloadUnit,
      setRowHeight,
    }),
    [initialized, aggregateField, aggregateTarget, workloadUnit, rowHeight]
  )
}
